import { Option } from "@inrev/core";
import { QueryClientProvider as QCProvider, QueryClient } from "@tanstack/react-query";
import { ReactNode, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { isAuthenticationError, logError } from "../utils/error";
import { useGlobalErrorMessageModal } from "./GlobalErrorHandlingProvider";

type RedirectOnError = (error: Error) => Option<string>;

// TanStack Query ignores a registered meta interface unless it extends Record<string, unknown>.
interface ErrorMeta extends Record<string, unknown> {
	// When true, failures are still logged to Sentry but won't surface the global
	// error modal. Use for non-critical requests.
	suppressErrorModal?: boolean;
	// Returns a path to navigate to on failure, or none to stay put.
	// Runs even when a mutation has its own onError.
	redirectOnError?: RedirectOnError;
}

interface MutationErrorMeta extends ErrorMeta {
	// Marks an optimistic list mutation that adds an item; read by usePendingIds.
	creates?: boolean;
}

declare module "@tanstack/react-query" {
	interface Register {
		queryMeta: ErrorMeta;
		mutationMeta: MutationErrorMeta;
	}
}

export const QueryClientProvider = ({
	queryClient,
	children,
}: {
	queryClient: QueryClient;
	children: ReactNode;
}) => {
	const { triggerErrorModal } = useGlobalErrorMessageModal();
	const navigate = useNavigate();

	useEffect(() => {
		const applyRedirect = (error: Error, redirectOnError?: RedirectOnError) => {
			const path = redirectOnError?.(error);
			if (Option.isSome(path)) navigate(path, { replace: true });
		};

		const queryCache = queryClient.getQueryCache();
		const unsubscribeQueries = queryCache.subscribe((event) => {
			if (event.type === "updated" && event.action.type === "error") {
				const eventStatus = event.query.state.status;
				const error = event.action.error;
				const suppressErrorModal = event.query.meta?.suppressErrorModal;
				const redirectOnError = event.query.meta?.redirectOnError;
				if (eventStatus !== "error") return;
				if (isAuthenticationError(error)) return;
				logError(error);
				if (!suppressErrorModal) triggerErrorModal(error);
				applyRedirect(error, redirectOnError);
			}
		});

		const mutationCache = queryClient.getMutationCache();
		const unsubscribeMutations = mutationCache.subscribe((event) => {
			if (event.type === "updated" && event.action.type === "error") {
				const eventStatus = event.mutation?.state.status;
				const error = event.action.error;
				const suppressErrorModal = event.mutation?.meta?.suppressErrorModal;
				const redirectOnError = event.mutation?.meta?.redirectOnError;
				const errorHandled = event.mutation?.options.onError;
				if (eventStatus !== "error") return;
				if (isAuthenticationError(error)) return;
				if (!errorHandled) {
					logError(error);
					if (!suppressErrorModal) triggerErrorModal(error);
				}
				applyRedirect(error, redirectOnError);
			}
		});

		return () => {
			unsubscribeQueries();
			unsubscribeMutations();
		};
	}, [queryClient, navigate, triggerErrorModal]);

	return <QCProvider client={queryClient}>{children}</QCProvider>;
};
